/// アプリのロール階層 (ADR 20260605)。優先度 super_admin > org_admin > member > readonly。
/// backend `Role` (domain/core/auth.rs) と 1:1。
export type AppRole = 'super_admin' | 'org_admin' | 'member' | 'readonly';

/// The authenticated caller. role / orgId は Auth0 id token claim から抽出する
/// (`features/auth/session/model/claims.ts`)。orgId はログイン中組織 = backend の
/// `session.org_id` に対応。super_admin は org 非依存で null になりうる。
export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: AppRole;
  orgId: string | null;
}

/// 顧客DBの編集が可能か (super_admin / org_admin / member)。
/// backend `has_permission(role, CustomerDbEdit)` の UI ミラー。
export function canEdit(role: AppRole): boolean {
  return role === 'super_admin' || role === 'org_admin' || role === 'member';
}

/// マスタの編集が可能か (super_admin のみ)。
/// backend `has_permission(role, MasterEdit)` の UI ミラー。
export function canEditMasters(role: AppRole): boolean {
  return role === 'super_admin';
}

/// テナント全体の管理 (組織CRUD / 全組織横断 / 除名 / 無効化・再有効化) が可能か。
/// backend `Action::AdminManageGlobal` = super_admin 専用の UI ミラー。
/// メンバーの無効化/再有効化ボタンなど global 操作のゲートに使う。
export function canManageTenant(role: AppRole): boolean {
  return role === 'super_admin';
}

/// メンバー一覧を閲覧できるか (super_admin = 全組織 / org_admin = 自組織のみ)。
/// backend `Action::AdminReadOrgMembers` の UI ミラー (ADR 20260605 org-scoped read)。
/// メンバー画面の表示 + 一覧 refresh のゲートに使う。
export function canViewMembers(role: AppRole): boolean {
  return role === 'super_admin' || role === 'org_admin';
}

/// 会員 (公開サイトのマイページ登録者) の一覧を閲覧できるか。
/// backend `Action::SiteMemberRead` の UI ミラー (super_admin / org_admin / member。readonly は不可)。
/// 社内メンバーを見る `canViewMembers` とは別の権限なので共用しない。
/// `canEdit` (顧客DB の編集) と許可ロールが同じでも流用しない — 「顧客DB を編集できる = 会員を
/// 読める」を自動で結び付けないため (backend が `CustomerDbRead` を流用せず専用 Action を
/// 足したのと同じ理由)。
export function canViewSiteMembers(role: AppRole): boolean {
  return role === 'super_admin' || role === 'org_admin' || role === 'member';
}

/// メンバーの招待 / ロール変更ができるか (super_admin = 任意組織 / org_admin = 自組織のみ)。
/// backend `Action::AdminInviteOrgMember` / `AdminManageOrgMemberRoles` の UI ミラー。
/// org スコープの一致強制と付与ロール上限は backend が真実 (canGrantRole は昇格ガードの UI ミラー)。
export function canManageMembers(role: AppRole): boolean {
  return role === 'super_admin' || role === 'org_admin';
}

/// `actor` が `target` ロールを付与できるか (招待 / ロール変更時)。
/// backend `can_grant_role` の UI ミラー = 権限昇格ガード。
/// super_admin: 全ロール / org_admin: org_admin・member のみ (super_admin 付与不可) / その他: 不可。
export function canGrantRole(actor: AppRole, target: AppRole): boolean {
  if (actor === 'super_admin') return true;
  if (actor === 'org_admin') return target === 'org_admin' || target === 'member';
  return false;
}

/// ロールの優先度ランク (super_admin > org_admin > member > readonly)。
/// backend `Role::privilege_rank` の UI ミラー。
const ROLE_RANK: Record<AppRole, number> = {
  super_admin: 3,
  org_admin: 2,
  member: 1,
  readonly: 0,
};

/// `actor` が「現在 `memberRole` を持つメンバー」のロールを変更できるか。
/// 被害者保護ガード (backend `ensure_can_manage_member_roles` の UI ミラー): 自分を上回る
/// ロール (org_admin から見た super_admin) を持つメンバーは改変不可。super_admin は誰でも可。
/// `memberRole` が未解決 (null) の場合は super_admin のみ許可 (fail closed)。
export function canManageMemberWithRole(actor: AppRole, memberRole: AppRole | null): boolean {
  if (actor === 'super_admin') return true;
  if (!canManageMembers(actor)) return false;
  if (memberRole === null) return false;
  return ROLE_RANK[memberRole] <= ROLE_RANK[actor];
}
